import { useParams, useSearchParams } from "react-router-dom";
import { getPartnerConfigBySource } from "../data/partnerConfigs";

interface PartnerConfig {
  partnerName?: string;
  referenceName?: string;
  partnerLogoUrl?: string;
}

/**
 * Page-type sub-route segments. When the second path segment is one of these,
 * the URL is a legacy `/:id/:pageType` route (e.g. `/copd-r/direct-redirect`),
 * NOT a `/:partnerSlug/:landingId` partner route — so no partner is inferred
 * from the path. Kept in sync with ROUTE_TO_PAGE_TYPE in pages/LandingPage.tsx.
 */
export const RESERVED_PAGE_SEGMENTS = new Set([
  "contact",
  "personal",
  "verify",
  "redirect",
  "no-sites",
  "qualified-redirect",
  "direct-redirect",
  "thank-you",
  "emr-consent",
]);

/**
 * Resolve the partner slug from the URL. Path-segment routing
 * (`/:partnerSlug/:landingId`) takes precedence over `?s=`. When the second
 * path segment is a reserved page-type word, the first segment is actually the
 * legacy landing id, so no partner is inferred from the path.
 */
export function resolvePartnerSlug(
  params: { partnerSlug?: string; landingId?: string },
  searchParams: URLSearchParams,
): string | undefined {
  if (
    params.partnerSlug &&
    params.landingId &&
    !RESERVED_PAGE_SEGMENTS.has(params.landingId)
  ) {
    return params.partnerSlug;
  }
  return searchParams.get("s") ?? undefined;
}

/**
 * Custom hook to get partner configuration from the URL.
 * Resolves the partner from the path prefix (`/:partnerSlug/:landingId`) first,
 * then falls back to the `s=` query param, and returns the corresponding
 * co-branding details.
 */
export function usePartnerConfig(): PartnerConfig {
  const params = useParams<{ partnerSlug?: string; landingId?: string }>();
  const [searchParams] = useSearchParams();

  const slug = resolvePartnerSlug(params, searchParams);
  const partner = getPartnerConfigBySource(slug);

  if (!partner) return {};

  return {
    partnerName: partner.partnerName,
    referenceName: partner.referenceName,
    partnerLogoUrl: partner.partnerLogoUrl,
  };
}

/**
 * Helper function to merge partner config with base config
 * Partner config from URL takes precedence over base config
 */
export function mergePartnerConfig(
  partnerConfig: PartnerConfig,
  baseConfig: { partnerName?: string; partnerLogoUrl?: string },
): PartnerConfig {
  return {
    partnerName: partnerConfig.partnerName || baseConfig.partnerName,
    partnerLogoUrl: partnerConfig.partnerLogoUrl || baseConfig.partnerLogoUrl,
  };
}
